/**
 * Real-time Store (Zustand)
 * ============================================================================
 *
 * Purpose:
 * --------
 * Zustand store for real-time WebSocket state management.
 *
 * Design Principles:
 * ------------------
 * - **Functional programming** with Zustand
 * - Immutable state updates
 * - Type-safe operations
 * - Integration with socket service
 *
 * Usage:
 * ------
 * ```typescript
 * import { useRealtimeStore } from 'core/realtime';
 *
 * function MyComponent() {
 *   const { status, connect, subscribe } = useRealtimeStore();
 *
 *   useEffect(() => {
 *     connect();
 *   }, []);
 * }
 * ```
 * ============================================================================
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import * as socketService from './socket.service';
import { CONNECTION_STATUS, MESSAGE_TYPE } from './constants';
import type {
  RealtimeStore,
  ConnectionState,
  Subscription,
  SubscriptionOptions,
  EventHandler,
  SocketMessage,
  QueuedMessage,
} from './types';

/**
 * Generate unique ID
 */
const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Real-time Store
 */
export const useRealtimeStore = create<RealtimeStore>()(
  devtools(
    (set, get) => ({
      // State
      connection: {
        status: CONNECTION_STATUS.DISCONNECTED,
        isConnected: false,
        isConnecting: false,
        isReconnecting: false,
        reconnectAttempts: 0,
        error: null,
        connectedAt: null,
        disconnectedAt: null,
        lastPing: null,
        lastPong: null,
        latency: null,
      },
      subscriptions: new Map(),
      messageQueue: [],
      socket: null,
      isAuthenticated: false,

      // Actions

      /**
       * Connect to WebSocket
       */
      connect: async () => {
        try {
          await socketService.connect();

          // Update connection state from service
          const connectionState = socketService.getConnectionState();
          set({ connection: connectionState });

          // Listen to connection events
          socketService.on('connect', () => {
            const state = socketService.getConnectionState();
            set({ connection: state, isAuthenticated: true });
          });

          socketService.on('disconnect', () => {
            const state = socketService.getConnectionState();
            set({ connection: state, isAuthenticated: false });
          });

          socketService.on('reconnect', () => {
            const state = socketService.getConnectionState();
            set({ connection: state, isAuthenticated: true });

            // Resubscribe to channels
            get().resubscribeAll();
          });
        } catch (error) {
          console.error('[RealtimeStore] Connection failed:', error);
        }
      },

      /**
       * Disconnect from WebSocket
       */
      disconnect: () => {
        socketService.disconnect();

        set({
          connection: {
            status: CONNECTION_STATUS.DISCONNECTED,
            isConnected: false,
            isConnecting: false,
            isReconnecting: false,
            reconnectAttempts: 0,
            error: null,
            connectedAt: null,
            disconnectedAt: new Date(),
            lastPing: null,
            lastPong: null,
            latency: null,
          },
          isAuthenticated: false,
        });
      },

      /**
       * Reconnect to WebSocket
       */
      reconnect: async () => {
        try {
          await socketService.reconnect();

          const connectionState = socketService.getConnectionState();
          set({ connection: connectionState, isAuthenticated: true });

          // Resubscribe to channels
          get().resubscribeAll();
        } catch (error) {
          console.error('[RealtimeStore] Reconnection failed:', error);
        }
      },

      /**
       * Send message
       */
      send: <T = unknown>(message: SocketMessage<T>) => {
        socketService.send(message);
      },

      /**
       * Subscribe to channel
       */
      subscribe: (
        channel: string,
        handler: EventHandler,
        options: SubscriptionOptions = {}
      ): string => {
        const subscriptionId = generateId();

        // Create subscription
        const subscription: Subscription = {
          id: subscriptionId,
          channel,
          handlers: new Map([[subscriptionId, handler]]),
          options: {
            autoResubscribe: true,
            ...options,
          },
          subscribedAt: new Date(),
          isActive: true,
        };

        // Add to store
        set((state) => {
          const newSubscriptions = new Map(state.subscriptions);
          newSubscriptions.set(subscriptionId, subscription);
          return { subscriptions: newSubscriptions };
        });

        // Subscribe via socket service
        const unsubscribe = socketService.on(channel, (payload) => {
          const sub = get().subscriptions.get(subscriptionId);

          if (sub && sub.isActive) {
            // Apply filter if exists
            if (sub.options?.filter && !sub.options.filter(payload)) {
              return;
            }

            // Filter by event types if specified
            if (sub.options?.events && payload.event) {
              if (!sub.options.events.includes(payload.event)) {
                return;
              }
            }

            // Call handler
            handler(payload);
          }
        });

        // Store unsubscribe function
        subscription.handlers.set('_unsubscribe', unsubscribe as any);

        // Send subscribe message to server
        if (get().connection.isConnected) {
          socketService.send({
            type: MESSAGE_TYPE.SUBSCRIBE,
            channel,
          });
        }

        return subscriptionId;
      },

      /**
       * Unsubscribe from channel
       */
      unsubscribe: (subscriptionId: string) => {
        const subscription = get().subscriptions.get(subscriptionId);

        if (!subscription) {
          return;
        }

        // Mark as inactive
        subscription.isActive = false;

        // Call unsubscribe function
        const unsubscribeFn = subscription.handlers.get('_unsubscribe');
        if (unsubscribeFn) {
          unsubscribeFn({} as any);
        }

        // Send unsubscribe message to server
        if (get().connection.isConnected) {
          socketService.send({
            type: MESSAGE_TYPE.UNSUBSCRIBE,
            channel: subscription.channel,
          });
        }

        // Remove from store
        set((state) => {
          const newSubscriptions = new Map(state.subscriptions);
          newSubscriptions.delete(subscriptionId);
          return { subscriptions: newSubscriptions };
        });
      },

      /**
       * Unsubscribe from all channels
       */
      unsubscribeAll: () => {
        const { subscriptions } = get();

        subscriptions.forEach((subscription) => {
          get().unsubscribe(subscription.id);
        });
      },

      /**
       * Resubscribe to all active channels
       */
      resubscribeAll: () => {
        const { subscriptions } = get();

        subscriptions.forEach((subscription) => {
          if (subscription.options?.autoResubscribe !== false) {
            socketService.send({
              type: MESSAGE_TYPE.SUBSCRIBE,
              channel: subscription.channel,
            });
          }
        });
      },

      /**
       * Listen to specific event
       */
      on: <T = unknown>(event: string, handler: EventHandler<T>): (() => void) => {
        return socketService.on(event, handler);
      },

      /**
       * Remove event listener
       */
      off: (event: string, handler: EventHandler) => {
        socketService.off(event, handler);
      },

      /**
       * Emit event
       */
      emit: <T = unknown>(event: string, data: T) => {
        socketService.emit(event, data);
      },

      /**
       * Update connection state
       */
      updateConnectionState: (updates: Partial<ConnectionState>) => {
        set((state) => ({
          connection: {
            ...state.connection,
            ...updates,
          },
        }));
      },

      /**
       * Add message to queue
       */
      queueMessage: (message: SocketMessage) => {
        const queuedMessage: QueuedMessage = {
          id: generateId(),
          message,
          retryCount: 0,
          queuedAt: new Date(),
          priority: message.priority || 1,
        };

        set((state) => ({
          messageQueue: [...state.messageQueue, queuedMessage],
        }));
      },

      /**
       * Process message queue
       */
      processMessageQueue: () => {
        const { messageQueue, send } = get();

        if (messageQueue.length === 0) {
          return;
        }

        // Sort by priority (higher first)
        const sortedQueue = [...messageQueue].sort((a, b) => b.priority - a.priority);

        // Send all messages
        sortedQueue.forEach((item) => {
          send(item.message);
        });

        // Clear queue
        set({ messageQueue: [] });
      },

      /**
       * Clear message queue
       */
      clearMessageQueue: () => {
        set({ messageQueue: [] });
      },
    }),
    { name: 'RealtimeStore' }
  )
);
